/* eslint-disable max-len */
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Draggable from 'react-draggable';
import * as ShotsActions from '../../../stores/actions/shots';
import * as BroadcastActions from '../../../stores/actions/broadcasting';
import useShots from '../../../hooks/use-shots';
import { GreenroomState } from '../../../stores/types/types';
import Switch from '../../general-components/switch';
import * as Storage from '../../../utils/local-storage';
import Socket from '../../../connection/socket';
import SlidingMenu, {
	SlidingMenuOption,
} from '../../general-components/sliding-menu';
import {
	exportMeetingJSON,
	importMeetingJSON,
} from '../../../stores/actions/meeting';
import { updateFeatureFlag } from '../../../stores/actions/settings';
import { APP } from '../../../stores/session-status';
import { BROADCAST_STATES } from '../../../stores/reducers/broadcasting';
import useAutoplay from '../../../hooks/use-autoplay';
import { Shot } from '../../../stores/reducers/shots';
import DebugVideo from './debug-video';
import { getCurrentShotIdEntity } from '../../../connection/shots';
import DebugScheduledStream from './debug-scheduled-stream';
import AVUI from '../av-ui';
import { setShowDeprecationWarning } from '../../../stores/actions/user-interface';
import UseQueryStrings from './query-strings';
import DebugBroadcast from './debug-broadcast';

export enum DebugLocalStorage {
	PanelEnabled = '_debug-panel-enabled',
	OverlaysOn = '_debug-panel-overlays-on',
	EchoCancellationOn = '_debug-panel-echo-cancellation-on',
	SocketCanDispatch = '_debug-panel-socket-can-dispatch',
	DebugOpentok = '_debug-opentok',
	debugKillServer = '_debug-kill-server',
	debugDisconnectProducer = '_debug-disconnect-producer',
	debugLogIsoAlerts = '_debug-log-iso-alerts',
}

interface LegacyChromeMemoryInfo {
	jsHeapSizeLimit: number;
	totalJSHeapSize: number;
	usedJSHeapSize: number;
}

interface LegacyChromeOnlyPerf extends Performance {
	memory: LegacyChromeMemoryInfo;
}

interface DebugPanelProps {
	socket: Socket | null;
}

const DEBUG_TABS = {
	MEMORY: 1,
	TOGGLES: 2,
	VIDEO: 3,
	SETTINGS: 5,
	EXPORT: 6,
	DEPENDENCIES: 7,
	AUTOPLAY: 8,
	SCHEDULED_STREAM: 9,
	BROADCAST: 10,
};

const tabOptions = [
	{ label: 'Memory', data: DEBUG_TABS.MEMORY },
	{ label: 'Toggles', data: DEBUG_TABS.TOGGLES },
	{ label: 'Settings', data: DEBUG_TABS.SETTINGS },
	{ label: 'Import / Export', data: DEBUG_TABS.EXPORT },
	{ label: 'Dependencies', data: DEBUG_TABS.DEPENDENCIES },
	{ label: 'Autoplay', data: DEBUG_TABS.AUTOPLAY },
	{ label: 'Video', data: DEBUG_TABS.VIDEO },
	{ label: 'Scheduled Stream', data: DEBUG_TABS.SCHEDULED_STREAM },
	{ label: 'Broadcast', data: DEBUG_TABS.BROADCAST },
];

const pattern = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];

export default function DebugPanel({ socket }: DebugPanelProps) {
	const dispatch = useDispatch();
	const draggableRef = useRef<HTMLDivElement>(null);

	const {
		debugEnabled,
		debugOverlaysEnabled,
		debugEchoCancellationEnabled,
		debugSocketCanDispatch,
		debugOpentok,
		debugKillServer,
		debugDisconnectProducer,
		debugLogIsoAlerts,
	} = useSelector((state: GreenroomState) => state.DebugReducer);

	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const featureFlags = useSelector((state: GreenroomState) => state.SettingsReducer.featureFlags);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const appSignedInWith = useSelector(
		(state: GreenroomState) => state.SessionReducer.appSignedInWith,
	);

	useEffect(() => {
		dispatch(setShowDeprecationWarning(false));
	}, []);

	UseQueryStrings();

	const { currentShot } = useShots();

	const { getShotAutoplay } = useAutoplay();

	const [isExpanded, setIsExpanded] = useState<boolean>(debugEnabled);
	const [tab, setTab] = useState<number>(DEBUG_TABS.TOGGLES);

	const [jsHeapSizeLimit, setJsHeapSizeLimit] = useState<number>(0);
	const [totalJSHeapSize, setTotalJSHeapSize] = useState<number>(0);
	const [usedJSHeapSize, setUsedJSHeapSize] = useState<number>(0);

	const [shouldSetRedux, setShouldSetRedux] = useState<boolean>(false);
	const [shouldSetSocket, setShouldSetSocket] = useState<boolean>(false);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [localLog, setLocalLog] = useState<string[]>([]);

	const [meetingJsonData, setMeetingJsonData] = useState<string | null>();

	const [watchAutoplayTimers, setWatchAutoplayTimers] = useState<boolean>(false);
	const [autoplayTimersText, setAutoplayTimersText] = useState<string>('');

	const setReduxInterval = useRef<NodeJS.Timeout | null>(null);
	const setSocketInterval = useRef<NodeJS.Timeout | null>(null);
	const setAutoplayTimersInterval = useRef<NodeJS.Timeout | null>(null);

	const getRoundedMemory = useCallback(
		(memory: number): number => Math.round(memory / 1000 ** 2),
		[],
	);

	const boolStr = useCallback((input: boolean): boolean => String(input).toLowerCase() === 'true', []);

	const setDebugPanelOpen = (isOpen: boolean) => {
		setIsExpanded(isOpen);
		Storage.setStorageItem(DebugLocalStorage.PanelEnabled, isOpen.toString());
	};

	const setOverlaysOn = (isEnabled: boolean) => {
		if (debugEnabled) {
			// eslint-disable-next-line no-constant-condition
			localLog.push(`Overlays Toggled ${isEnabled}` ? 'on' : 'off');
			Storage.setStorageItem(DebugLocalStorage.OverlaysOn, isEnabled.toString());
			dispatch({ type: 'DEBUG_TOGGLE_OVERLAYS', payload: isEnabled });
		}
	};

	const toggleEchoCancellation = (isEnabled: boolean) => {
		if (debugEnabled) {
			// eslint-disable-next-line no-constant-condition
			localLog.push(`Echo Cancellation Toggled ${isEnabled}` ? 'on' : 'off');
			Storage.setStorageItem(DebugLocalStorage.EchoCancellationOn, isEnabled.toString());
			dispatch({ type: 'DEBUG_TOGGLE_ECHO_CANCELLATION', payload: isEnabled });
		}
	};

	const resetLocalStorage = () => {
		Object.values(DebugLocalStorage).forEach((key) => Storage.removeStorageItem(key));
	};

	const toggleSocketCanDispatch = (isEnabled: boolean) => {
		if (debugEnabled) {
			// eslint-disable-next-line no-constant-condition
			localLog.push(`Overlays Toggled ${isEnabled}` ? 'on' : 'off');
			Storage.setStorageItem(DebugLocalStorage.SocketCanDispatch, isEnabled.toString());
			dispatch({ type: 'DEBUG_TOGGLE_SOCKET_DISPATCH', payload: isEnabled });
		}
	};

	const toggleDebugOpentok = (isEnabled: boolean) => {
		if (debugEnabled) {
			// eslint-disable-next-line no-constant-condition
			localLog.push(`Overlays Toggled ${isEnabled}` ? 'on' : 'off');
			Storage.setStorageItem(DebugLocalStorage.DebugOpentok, isEnabled.toString());
			dispatch({ type: 'DEBUG_OPENTOK_DISPATCH', payload: isEnabled });
		}
	};

	const toggleDebugKillServer = (isEnabled: boolean) => {
		if (debugEnabled) {
			// eslint-disable-next-line no-constant-condition
			localLog.push(`Server Kill State Toggled ${isEnabled}` ? 'on' : 'off');
			Storage.setStorageItem(DebugLocalStorage.debugKillServer, isEnabled.toString());
			dispatch({ type: 'DEBUG_TOGGLE_KILL_SERVER', payload: isEnabled });
			if (!debugKillServer) {
				dispatch(BroadcastActions.setBroadcastState(BROADCAST_STATES.KILLED));
			} else {
				dispatch(BroadcastActions.setBroadcastState(BROADCAST_STATES.BROADCAST_STOPPED));
			}
		}
	};

	const phrases = useRef(['Hello world.', 'https://www.youtube.com/watch?v=dQw4w9WgXcQ']);
	useEffect(() => { if (new Date().getDay() === 3) phrases.current.push('It is Wednesday my dudes'); }, []);
	const rand = (list: Array<any>) => list[Math.floor((Math.random() * list.length))];
	const [konamiOn, setKonami] = useState<boolean>(false);
	const match = useRef<number>(0);
	const konami = (e: any) => {
		match.current = (pattern.indexOf(e.key) < 0 || e.key !== pattern[match.current]) ? 0 : match.current + 1;
		if (match.current === pattern.length) { console.log(rand(phrases.current)); match.current = 0; }
	};
	useEffect(() => {
		if (konamiOn) document.addEventListener('keydown', konami, false);
		else document.removeEventListener('keydown', konami);
		return () => document.removeEventListener('keydown', konami);
	}, [konamiOn]);

	const toggleDebugDisconnectProducer = (isEnabled: boolean) => {
		if (debugEnabled) {
			// eslint-disable-next-line no-constant-condition
			localLog.push(`Producer Connected State Toggled ${isEnabled}` ? 'on' : 'off');
			Storage.setStorageItem(DebugLocalStorage.debugDisconnectProducer, isEnabled.toString());
			dispatch({ type: 'DEBUG_TOGGLE_DISCONNECT_PRODUCER', payload: isEnabled });
		}
	};

	const toggleDebugLogIsoAlerts = (isEnabled: boolean) => {
		if (debugEnabled) {
			// eslint-disable-next-line no-constant-condition
			localLog.push(`Log Iso Alerts State Toggled ${isEnabled}` ? 'on' : 'off');
			Storage.setStorageItem(DebugLocalStorage.debugLogIsoAlerts, isEnabled.toString());
			dispatch({ type: 'DEBUG_TOGGLE_LOG_ISO_ALERTS', payload: isEnabled });
		}
	};

	useEffect(() => {
		const perf = window.performance as LegacyChromeOnlyPerf;

		const memInterval = setInterval(() => {
			if (perf?.memory) {
				setJsHeapSizeLimit(getRoundedMemory(perf.memory.jsHeapSizeLimit));
				setTotalJSHeapSize(getRoundedMemory(perf.memory.totalJSHeapSize));
				setUsedJSHeapSize(getRoundedMemory(perf.memory.usedJSHeapSize));
			}
		}, 10000);

		// Load Settings
		const panelOpen = Storage.getStorageItem(DebugLocalStorage.PanelEnabled);
		const overlaysOn = Storage.getStorageItem(DebugLocalStorage.OverlaysOn);
		const echoCancellationOn = Storage.getStorageItem(DebugLocalStorage.EchoCancellationOn);
		const socketCanDispatch = Storage.getStorageItem(DebugLocalStorage.SocketCanDispatch);
		const debugOpentokOn = Storage.getStorageItem(DebugLocalStorage.DebugOpentok);

		// On by default
		setDebugPanelOpen(panelOpen !== null ? boolStr(panelOpen) : true);
		setOverlaysOn(overlaysOn !== null ? boolStr(overlaysOn) : true);
		toggleEchoCancellation(echoCancellationOn !== null ? boolStr(echoCancellationOn) : true);
		toggleSocketCanDispatch(socketCanDispatch !== null ? boolStr(socketCanDispatch) : true);
		toggleDebugOpentok(debugOpentokOn !== null ? boolStr(debugOpentokOn) : true);

		// Off by default
		// toggleDebugOpentok(debugOpentokOn !== null ? boolStr(debugOpentokOn) : true);

		return () => {
			clearInterval(memInterval);
		};
	}, []);

	useEffect(() => {
		if (shouldSetRedux) {
			setReduxInterval.current = setInterval(() => {
				for (let i = 0; i < 10; i += 1) {
					localLog.push('setShotModalShot');
					dispatch(ShotsActions.setShotModalShot({ ...currentShot }));
				}
			}, 100);
		} else if (setReduxInterval.current) {
			clearInterval(setReduxInterval.current);
		}
	}, [shouldSetRedux]);

	useEffect(() => {
		if (shouldSetSocket) {
			setSocketInterval.current = setInterval(() => {
				localLog.push('trigger-load-shots');
				socket?.sendMessage('trigger-load-shots', {});
			}, 0);
		} else if (setSocketInterval.current) {
			clearInterval(setSocketInterval.current);
		}
	}, [shouldSetSocket]);

	const disconnectSocketListeners = () => {
		socket?.removeAllListeners();
	};

	const exportRoomToJson = () => {
		if (!meeting) {
			// eslint-disable-next-line no-alert
			alert('There is no meeting or current shot...');
			return;
		}

		if (!token) {
			// eslint-disable-next-line no-alert
			alert('There is no token...');
			return;
		}

		const {
			meeting: meetingId,
			title,
		} = meeting;

		dispatch(exportMeetingJSON(token, meetingId, title));
	};

	const validateMeetingJson = (event: any) => {
		const selectedFile = event.target.files[0];
		const reader = new FileReader();
		reader.onload = (readerEvent) => {
			if (readerEvent?.target?.result) {
				setMeetingJsonData(readerEvent.target.result.toString());
			}
		};
		reader.readAsText(selectedFile);
	};

	const importJson = (): void => {
		if (!token || !meeting) return;
		if (!meetingJsonData || meetingJsonData === '') {
			// eslint-disable-next-line no-alert
			alert('You must add json data');
			return;
		}

		try {
			// validate is json data
			JSON.parse(meetingJsonData);

			// create new meeting in current channel
			const { channel } = meeting;

			dispatch(importMeetingJSON(token, meetingJsonData, channel, (result: any) => {
				const code = result?.newMeeting?.meeting_id;
				window.location.href = `/producer/${code}`;
			}));
		} catch (e) {
			const message = e instanceof Error
				? e.message
				: 'Unknown error';
			// eslint-disable-next-line no-console
			console.error(e);
			// eslint-disable-next-line no-alert
			alert(`Invalid JSON => ${message}`);
		}
	};

	const [FFLabels, FFMap] = useMemo(() => {
		const newFFMap = new Map(Object.entries(featureFlags));
		const newFFLabels = Array.from(newFFMap.keys());
		return [newFFLabels, newFFMap];
	}, [featureFlags]);

	const checkRemoteShotEntity = async (currentValue: boolean | undefined) => {
		if (!currentValue && token && meeting?.meeting) {
			// usually would be called in use-join but put here to not impact
			const currentShotIdEntity = await getCurrentShotIdEntity(token, meeting?.meeting);

			if (currentShotIdEntity) {
				dispatch({ type: currentShotIdEntity.entityType, payload: currentShotIdEntity });
			}
		}
	};

	const toggleFeatureFlag = async (flagLabel: string, currentValue: boolean | undefined) => {
		if (currentValue === undefined) return;
		dispatch(updateFeatureFlag([flagLabel, !currentValue]));
		checkRemoteShotEntity(currentValue);
	};

	useEffect(() => {
		const getAutoplayTimers = async (shot: Shot) => {
			const autoplayTimers = await getShotAutoplay(shot);

			if (autoplayTimers) {
				const {
					id,
					durationLeft,
				} = autoplayTimers;

				setAutoplayTimersText(`${id}: ${durationLeft}`);
			} else {
				setWatchAutoplayTimers(false);
			}
		};

		if (watchAutoplayTimers) {
			setAutoplayTimersInterval.current = setInterval(() => {
				getAutoplayTimers(currentShot);
			}, 1000);
		} else if (setAutoplayTimersInterval.current) {
			clearInterval(setAutoplayTimersInterval.current);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watchAutoplayTimers]);

	return (<>
		<Draggable nodeRef={draggableRef}>
			<div
			ref={draggableRef}
			className={classNames('debug-panel-container', { expanded: isExpanded })}
		>
				<div
					className="debug-expander"
					onClick={() => setDebugPanelOpen(!isExpanded)}
				>
					<span>Debug Panel</span>
					<span className="small">
						&nbsp;(click to {!isExpanded ? 'show' : 'minimize'}, drag to drag)
					</span>
				</div>
				{isExpanded && <div className="debug-panel">
					<SlidingMenu
						options={tabOptions}
						selectedOption={tabOptions.find(
							(op: SlidingMenuOption) => op.data === tab,
						) ?? tabOptions[0]}
						callback={(option: SlidingMenuOption) => { setTab(option.data); }}
						className='position-relative width-calc-full'
						fullWidthDivider={true}
					/>
					<div className='debug-panel-below-menu'>
						{tab === DEBUG_TABS.MEMORY && <>
							<div className="section memory-dashboard">
								<h5>Inexact Memory Usage</h5>
								<div className="memory-row">
									Memory Size Limit: ~{jsHeapSizeLimit} mb
								</div>
								<div className="memory-row">
									Memory Allocated: ~{totalJSHeapSize} mb
								</div>
								<div className="memory-row">Memory Active: ~{usedJSHeapSize} mb</div>
							</div>
							<div className="section memory-controls">
								<h5>Spam Memory Usage</h5>
								<button
									className="padding-small background-green text-dark border-radius-small"
									onClick={() => setShouldSetRedux((current) => !current)}
								>
									{!shouldSetRedux ? 'Start Redux Updates' : 'Stop Redux Updates'}
								</button>
							</div>
							<div className="section memory-controls">
								<button
									className="padding-small background-green text-dark border-radius-small"
									onClick={() => setShouldSetSocket((current) => !current)}
								>
									{!shouldSetSocket ? 'Start Socket Updates' : 'Stop Socket Updates'}
								</button>
							</div>
							<a className="blue" href="https://yonatankra.com/measuring-used-js-heap-size/">More on memory</a>

						</>}

						{tab === DEBUG_TABS.TOGGLES && <>
							<div className="section settings">
								<h5>Feature Flags</h5>
								<div style={{ maxWidth: '100%', marginBottom: '8px' }}>
									<div>These will toggle for your current session.</div>
									<div>Refreshing will re-fetch FFs from DB.</div>
									<div>Updates to FF's in the DB will update your local app automatically.</div>
									<div>This does NOT update other apps, such as APIs or websocket servers.</div>
								</div>
								<div className="advanced-settings-container">
									{FFLabels.map((flagLabel: string) => (
										<div key={flagLabel} className="align-items-center justify-content-space-between vertical-padding-small">
											<span>{flagLabel}</span>
											<Switch
												on={FFMap?.get(flagLabel) ?? false}
												onChange={() => toggleFeatureFlag(flagLabel, FFMap.get(flagLabel))}
												className={'green'}
											/>
										</div>))}
								</div>
							</div>
							<div className="section settings">
								<h5>Settings</h5>
								<div className="advanced-settings-container">
									<div className="align-items-center justify-content-space-between vertical-padding-small">
										<span>Overlays</span>
										<Switch
											on={debugOverlaysEnabled}
											onChange={() => setOverlaysOn(!debugOverlaysEnabled)}
											className={'green'}
										/>
									</div>
									<div className="align-items-center justify-content-space-between vertical-padding-small">
										<span>Echo Cancellation</span>
										<Switch
											on={debugEchoCancellationEnabled}
											onChange={() => toggleEchoCancellation(!debugEchoCancellationEnabled)}
											className={'green'}
										/>
									</div>
									<div className="align-items-center justify-content-space-between vertical-padding-small">
										<span>Socket can fire dispatch</span>
										<Switch
											on={debugSocketCanDispatch}
											onChange={() => toggleSocketCanDispatch(!debugSocketCanDispatch)}
											className={'green'}
										/>
									</div>
									<div className="align-items-center justify-content-space-between vertical-padding-small">
										<span>Debug Opentok</span>
										<Switch
											on={debugOpentok}
											onChange={() => toggleDebugOpentok(!debugOpentok)}
											className={'green'}
										/>
									</div>
									<div className="align-items-center justify-content-space-between vertical-padding-small">
										<span>Disconnect Producer</span>
										<Switch
											on={debugDisconnectProducer}
											onChange={() => toggleDebugDisconnectProducer(!debugDisconnectProducer)}
											className={'green'}
										/>
									</div>
									<div className="align-items-center justify-content-space-between vertical-padding-small">
										<span>Kill Server 💀</span>
										<Switch
											on={debugKillServer}
											onChange={() => toggleDebugKillServer(!debugKillServer)}
											className={'green'}
										/>
									</div>
									<div className="align-items-center justify-content-space-between vertical-padding-small">
										<span>Konami? 👾</span>
										<Switch
											on={konamiOn}
											onChange={() => setKonami((prev) => !prev)}
											className={'green'}
										/>
									</div>

									<div className="align-items-center justify-content-space-between vertical-padding-small">
										<span>Log timestamps of ISO Alerts</span>
										<Switch
											on={debugLogIsoAlerts}
											onChange={() => toggleDebugLogIsoAlerts(!debugLogIsoAlerts)}
											className={'green'}
										/>
									</div>
								</div>
							</div>
						</>}

						{tab === DEBUG_TABS.SETTINGS && <>
							<div className="section reset">
								<span>Removes all socket listeners. Refresh to re-enable listeners.</span>
								<button
									className="padding-small background-green text-dark border-radius-small"
									onClick={disconnectSocketListeners}
									title="Removes all socket listeners. Refresh to re-enable listeners."
								>
									Disconnect Socket Listeners
								</button>
							</div>
							<div className="section reset">
								<span>Clears the debug flags in local storage.
									Turning off this panel from settings will have the same effect.</span>
								<button
									className="padding-small background-green text-dark border-radius-small"
									onClick={resetLocalStorage}
									title="Clears the debug flags in local storage. Turning off this panel from settings will
									have the same effect."
								>
									Clear Debug Local Storage Objects
								</button>
							</div>

							{/* <div style={{ maxHeight: '200px', maxWidth: '100px' }}>
								{localLog.map((log: string, index: number) => {
									return <div key={`${log}_${index}`}>{log}</div>;
								})}
							</div> */}
						</>}

						{tab === DEBUG_TABS.EXPORT && (
							<>
								<div className="section reset" style={{ marginBottom: '16px' }}>
									<h5>Import Greenroom JSON</h5>
									<div style={{ maxWidth: '100%', marginBottom: '16px' }}>
										<div>Creates a brand new meeting using the same channel.</div>
										<div>Meetings can only be imported using Standalone.</div>
										<div>User must have a Brandlive Email Address to import.</div>
									</div>
									<input
										type="file"
										id="meeting-import-file"
										onChange={validateMeetingJson}
										style={{ marginBottom: '1rem' }}
										disabled={appSignedInWith !== APP.GREENROOM}
									/>
									<button
										className="padding-small background-green text-dark border-radius-small"
										onClick={importJson}
										title="Import Room JSON export elsewhere."
										disabled={appSignedInWith !== APP.GREENROOM}
									>
										Import Room
									</button>
								</div>
								<div className="section reset">
									<h5>Export Greenroom JSON</h5>
									<div style={{ maxWidth: '100%', marginBottom: '16px' }}>
										<div>Both Brandlive and Standalone meetings can be exported.</div>
									</div>
									<button
										className="padding-small background-green text-dark border-radius-small"
										onClick={exportRoomToJson}
										title="Export Room in JSON to be imported else where."
									>
										Export Room
									</button>
								</div>
							</>
						)}

						{tab === DEBUG_TABS.DEPENDENCIES && (
							<>
								<div title="Not over me silly, the buttons">Import Greenroom JSON</div>
								<div title="Not over me silly, the buttons">Do not import brandlive file to standalone or vice versa.</div>
								<div className="section reset">
									<iframe
										src={'/dev-info-package.html'}
										style={{
											backgroundColor: '#A6FF79',
											fontSize: '10pt',
											height: '420px',
											width: '505px',
										}}
									/>
								</div>
							</>
						)}

						{tab === DEBUG_TABS.AUTOPLAY && (
							<>
								<div className="section">
									<h5>Current Shot Timer</h5>
								</div>
								<div className="section">
									<button
									className="padding-small background-green text-dark border-radius-small"
										onClick={() => setWatchAutoplayTimers(
											(watching) => !watching,
										)}
									>
										{`${!watchAutoplayTimers ? 'Start' : 'Stop'} Monitoring`}
									</button>
									<pre className="code">
										{(watchAutoplayTimers && autoplayTimersText) ? (
											<>
												&#123;{'\n'}
													{'    '}{autoplayTimersText}
												{'\n'}&#125;
											</>
										) : (
											<>Not monitoring</>
										)}
									</pre>
								</div>
							</>
						)}

						{tab === DEBUG_TABS.VIDEO && (
							<>
								<DebugVideo/>
							</>
						)}

						{tab === DEBUG_TABS.SCHEDULED_STREAM && (
							<DebugScheduledStream />
						)}

						{tab === DEBUG_TABS.BROADCAST && (
							<DebugBroadcast />
						)}
					</div>
				</div>}

			</div>
		</Draggable>
		<AVUI />
	</>);
}
